import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  NgZone,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { v4 } from 'uuid';
import { environment } from '../../../environments';
import {
  ITrackingAction,
  ITrackingObject,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { WindowRefService } from 'i2g-ng-utils';
import { IconSizesEnum, IIcon } from 'i2g-ng-ui';

export interface IOauthFacebookInfo {
  email: string;
  idToken: string;
}

@Component({
  selector: 'app-oauth-facebook-circle',
  templateUrl: 'oauth-facebook-circle.component.html',
  styleUrls: ['oauth-facebook-circle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OauthFacebookCircleComponent implements OnInit, OnDestroy {
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output() authenticationSuccess = new EventEmitter<IOauthFacebookInfo>();
  @Output() authenticationError = new EventEmitter<Error>();

  facebookIcon: IIcon;
  id: string;
  initialized$ = new BehaviorSubject(false);
  processing$ = new BehaviorSubject(false);

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _translateService: TranslateService,
    private _ngZone: NgZone,
    private _windowRefService: WindowRefService
  ) {}

  get _fb() {
    return this._windowRefService.nativeWindow.FB;
  }

  ngOnInit() {
    this.id = v4();

    this.facebookIcon = {
      name: 'facebook-round',
      size: IconSizesEnum.custom,
    };

    const win = this._windowRefService.nativeWindow;
    timer(0, 1000)
      .pipe(
        takeUntil(this._destroyed),
        map(() => win.FB),
        filter((x) => !!x),
        take(1)
      )
      .subscribe(() => {
        this._fb.init({
          appId: environment.facebookAppId,
          version: 'v3.2',
        });
        this.initialized$.next(true);
      });
  }

  signInToFacebook() {
    this.processing$.next(true);

    this._fb.login(
      (loginResponse) => {
        this._ngZone.run(() => {
          if (loginResponse.status === 'connected') {
            const idToken = loginResponse.authResponse.accessToken;
            const url = `/me?fields=email`;
            this._fb.api(url, (response) => {
              this._ngZone.run(() => {
                const email = response.email;
                if (email) {
                  this.authenticationSuccess.emit({ email, idToken });
                } else {
                  // User logged in Facebook with phone number and the account doesn't have email.
                  const msg = this._translateService.instant(
                    'OAUTH_FACEBOOK.ACCOUNT_WITHOUT_EMAIL'
                  );
                  this.authenticationError.emit(new Error(msg));
                }
                this.processing$.next(false);
              });
            });
          } else {
            this.processing$.next(false);
          }
        });
      },
      { scope: 'public_profile,email' }
    );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
